/* eslint-disable camelcase */
import ApiService from 'api-client/ApiService'
import { GetServerPoolsResponse } from 'app/plugins/openstack/components/storage/pools/model'

class Cinder extends ApiService {
  public getClassName() {
    return 'cinder'
  }

  protected async getEndpoint() {
    return this.client.keystone.getServiceEndpoint('cinderv3', 'public')
  }

  volumesUrl = () => '/volumes'

  defaultVolueType = async () => {
    const endpoint = await this.getEndpoint()
    return endpoint.slice(0, endpoint.indexOf('v3'))
  }

  async checkHealth() {
    return this.client.basicGet<any>({
      // Remove the tenantId from the endpoint
      endpoint: (await this.getEndpoint())
        ?.split('/')
        ?.slice(0, -1)
        ?.join('/'),
      url: `/`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'checkHealth',
      },
    })
  }

  async getRegionUrls() {
    const services = (await this.client.keystone.getServiceCatalog()).find(
      (x) => x.name === 'cinderv3',
    ).endpoints
    const baseUrlsByRegion = services.reduce((accum, service) => {
      accum[service.region] = service.url
      return accum
    }, {})
    return baseUrlsByRegion
  }

  async getVolume(id) {
    const projectId = this.client.activeProjectId
    const response = await this.client.basicGet<any>({
      url: `/volumes/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackVolume',
      },
    })
    return response.volume
  }

  async getSimpleVolumes() {
    const volumes = []
    const getVolumes = async (href = '') => {
      const endpoint = href ? href?.split('/volumes')?.[1] : null
      const response = await this.client.basicGet<any>({
        url: endpoint ? `/volumes${endpoint}` : '/volumes',
        options: {
          clsName: this.getClassName(),
          mthdName: 'getSimpleOpenstackVolumes',
        },
      })
      const vols = response?.volumes
      volumes.push(...vols)
      if (response?.volumes_links?.[0]?.href) {
        await getVolumes(response.volumes_links[0].href)
      }
    }
    await getVolumes()
    return volumes
  }

  // Get volumes with details
  async getVolumes() {
    const projectId = this.client.activeProjectId
    const response = await this.client.basicGet<any>({
      url: `/volumes/detail`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackVolumes',
      },
    })
    return response.volumes
  }

  async getVolumesAllPages() {
    const volumes = []
    const getVolumes = async (href = '') => {
      const endpoint = href ? href?.split('/volumes')?.[1] : null
      const response = await this.client.basicGet<any>({
        url: endpoint ? `/volumes${endpoint}` : '/volumes/detail',
        options: {
          clsName: this.getClassName(),
          mthdName: 'getOpenstackVolumes',
        },
      })
      const vols = response?.volumes
      volumes.push(...vols)
      if (response?.volumes_links?.[0]?.href) {
        await getVolumes(response.volumes_links[0].href)
      }
    }
    await getVolumes()
    return volumes
  }

  async getAllVolumes() {
    const response = await this.client.basicGet<any>({
      url: `/volumes/detail?all_tenants=1`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAllVolumes',
      },
    })
    return response.volumes
  }

  async getAllVolumesAllPages() {
    const volumes = []
    const getVolumes = async (href = '') => {
      const endpoint = href ? href?.split('/volumes')?.[1] : null
      const response = await this.client.basicGet<any>({
        url: endpoint ? `/volumes${endpoint}` : '/volumes/detail?all_tenants=1',
        options: {
          clsName: this.getClassName(),
          mthdName: 'getAllVolumes',
        },
      })
      const vols = response?.volumes
      volumes.push(...vols)
      if (response?.volumes_links?.[0]?.href) {
        await getVolumes(response.volumes_links[0].href)
      }
    }
    await getVolumes()
    return volumes
  }

  async getAllVolumesCount(limit, allTenants, markerId) {
    const baseUrl = `${this.volumesUrl()}/detail`
    const limitUrl = `?limit=${limit}`
    const projectUrl = allTenants ? '&all_tenants=1' : ''
    const markerUrl = markerId ? `&marker=${markerId}` : ''
    const url = baseUrl + limitUrl + projectUrl + markerUrl
    const response = await this.client.basicGet<any>({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAllVolumesCount',
      },
    })
    return response.volumes
  }

  async createVolume(body) {
    const projectId = this.client.activeProjectId
    try {
      const response = await this.client.basicPost<any>({
        url: `/volumes`,
        body,
        options: {
          clsName: this.getClassName(),
          mthdName: 'createVolume',
        },
      })
      return response.volume
    } catch (err) {
      return err.response.data
    }
  }

  async deleteVolume(id, isForceCascade = false, isCascade = false) {
    const projectId = this.client.activeProjectId
    const queryParams = isForceCascade
      ? '?force=true&cascade=true'
      : isCascade
      ? '?cascade=true'
      : ''

    return this.client.basicDelete<any>({
      url: `/volumes/${id}${queryParams}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteVolume',
        config: {
          headers: {
            'OpenStack-API-Version': 'volume 3.70',
          },
        },
      },
    })
  }

  async updateVolume(id, body) {
    const response = await this.client.basicPut<any>({
      url: `/volumes/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateVolume',
      },
    })
    return response.volume
  }

  async setBootable(id, bool) {
    const response = await this.client.basicPost<any>({
      url: `${this.volumesUrl()}/${id}/action`,
      body: {
        'os-set_bootable': { bootable: bool },
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'setBootable',
      },
    })
    return response.volume
  }

  setVolumeMetadata = async ({ id, body }) => {
    const response = await this.client.basicPut<any>({
      url: `/volumes/${id}/metadata`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateVolumeMetadata',
      },
    })
    return response.metadata
  }

  async retypeVolume({ id, body }) {
    const response = await this.client.basicPost<any>({
      url: `${this.volumesUrl()}/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'retypeVolume',
      },
    })
    return response.volume
  }

  async migrateVolume({ id, body }) {
    const response = await this.client.basicPost<any>({
      url: `${this.volumesUrl()}/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'migrateVolume',
      },
    })
    return response.volume
  }

  // TODO: Test case for extend function
  // TODO: Current API doesn't work on AWS. Need to implement check logic in test.
  async extendVolume(id, size) {
    const response = await this.client.basicPost<any>({
      url: `${this.volumesUrl()}/${id}/action`,
      body: {
        'os-extend': { new_size: size },
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'extendVolume',
        config: {
          headers: {
            'OpenStack-API-Version': 'volume 3.70',
          },
        },
      },
    })
    return response
  }

  // TODO: test case for reset function (Instance implement needed. Attach function needed?)
  async resetVolumeStatus(id, body) {
    const response = await this.client.basicPost<any>({
      url: `${this.volumesUrl()}/${id}/action`,
      body: body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'resetVolumeStatus',
      },
    })
    return response.volume
  }

  async uploadVolumeAsImage({ id, body }) {
    const response = await this.client.basicPost<any>({
      url: `/volumes/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: `uploadVolumeAsImage`,
      },
    })
    return response
  }

  async getVolumeTypes() {
    const projectId = this.client.activeProjectId
    const response = await this.client.basicGet<any>({
      url: `/types`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getVolumeTypes',
      },
    })
    return response.volume_types
  }

  async getPrivateVolumeTypes() {
    const projectId = this.client.activeProjectId
    const response = await this.client.basicGet<any>({
      url: `/types?is_public=false`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getPrivateVolumeTypes',
      },
    })
    return response.volume_types
  }

  async getVolumeType(id) {
    const projectId = this.client.activeProjectId
    const response = await this.client.basicGet<any>({
      url: `/types/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getVolumeType',
      },
    })
    return response.volume_type
  }

  async createVolumeType(body) {
    const response = await this.client.basicPost<any>({
      url: `/types`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createVolumeType',
      },
    })
    return response.volume_type
  }

  async deleteVolumeType(id) {
    await this.client.basicDelete<any>({
      url: `/types/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteVolumeType',
      },
    })
  }

  async updateVolumeType(id, params, keysToDelete = []) {
    const { extra_specs: extraSpecs, ...rest } = params
    await this.client.basicPost<any>({
      url: `/types/${id}/extra_specs`,
      body: {
        extra_specs: extraSpecs,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateVolumeType/extra_specs',
      },
    })
    await Promise.all(
      keysToDelete.map(async (key) => {
        return this.client.basicDelete<any>({
          url: `/types/${id}/extra_specs/${key}`,
          options: {
            clsName: this.getClassName(),
            mthdName: `updateVolumeType/delete/${key}`,
          },
        })
      }),
    )
    const baseResponse = await this.client.basicPut<any>({
      url: `/types/${id}`,
      body: {
        volume_type: rest,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: 'updateVolumeType',
      },
    })
    return baseResponse
  }

  async getDefaultTenantVolumeType(projectId = undefined) {
    const projectIdLocal = projectId ? projectId : this.client.activeProjectId
    const response = await this.client.basicGet<any>({
      endpoint: await this.defaultVolueType(),
      url: `/v3/default-types/${projectIdLocal}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getDefaultTenantVolumeType',
        config: {
          headers: { 'OpenStack-API-Version': 'volume 3.70' },
        },
      },
    })
    return response.default_type
  }

  async setDefaultVolumeType(id, defaultType) {
    const response = await this.client.basicPut<any>({
      endpoint: await this.defaultVolueType(),
      url: `/v3/default-types/${id}`,
      body: { default_type: defaultType },
      options: {
        clsName: this.getClassName(),
        mthdName: 'setDefaultVolumeType',
        config: {
          headers: { 'OpenStack-API-Version': 'volume 3.70' },
        },
      },
    })
    return response.volume_type
  }

  async unsetVolumeTypeTag(id, tag) {
    await this.client.basicDelete<any>({
      url: `/types/${id}/extra_specs/${tag}`,
      options: {
        clsName: this.getClassName(),
        mthdName: `unsetVolumeTypeTag`,
      },
    })
  }

  getVolumeTypeTenants = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/types/${id}/os-volume-type-access`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getVolumeTypeTenants',
      },
    })
    return response.volume_type_access
  }

  addVolumeTypeTenant = async ({ id, body }) => {
    const response = await this.client.basicPost<any>({
      url: `/types/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'addVolumeTypeTenant',
      },
    })
    return response
  }

  removeVolumeTypeTenant = async ({ id, body }) => {
    const response = await this.client.basicPost<any>({
      url: `/types/${id}/action`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'removeVolumeTypeTenant',
      },
    })
    return response
  }

  async getVolumeSnapshot(id) {
    const projectId = this.client.activeProjectId
    const response = await this.client.basicGet<any>({
      url: `/snapshots/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getOpenstackVolumeSnapshot',
      },
    })
    return response.snapshot
  }

  async getVolumeSnapshots() {
    const projectId = this.client.activeProjectId
    const response = await this.client.basicGet<any>({
      url: `/snapshots/detail`,
      options: {
        clsName: this.getClassName(),
        mthdName: `getOpenstackVolumeSnapshots`,
      },
    })
    return response.snapshots
  }

  async getSnapshotsForVolume(volumeId) {
    const response = await this.client.basicGet<any>({
      url: `/snapshots/detail?volume_id=${volumeId}`,
      options: {
        clsName: this.getClassName(),
        mthdName: `getSnapshotsForVolume`,
      },
    })
    return response.snapshots
  }

  // async getAllSnapshots() {
  //   const response = await this.client.basicGet<any>({
  //     url: `/snapshots/detail?all_tenants=1`,
  //     options: {
  //       clsName: this.getClassName(),
  //       mthdName: `getAllSnapshots`,
  //     },
  //   })
  //   return response.snapshots
  // }

  async snapshotVolume(body) {
    const projectId = this.client.activeProjectId
    const response = await this.client.basicPost<any>({
      url: `/snapshots`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: `snapshotVolume`,
      },
    })
    return response.snapshot
  }

  // async deleteSnapshot(id) {
  //   await this.client.basicDelete<any>({
  //     url: `/snapshots/${id}`,
  //     options: {
  //       clsName: this.getClassName(),
  //       mthdName: `deleteSnapshot`,
  //     },
  //   })
  // }

  async deleteVolumeSnapshot(id) {
    const projectId = this.client.activeProjectId
    return this.client.basicDelete<any>({
      url: `/snapshots/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteVolumeSnapshot',
      },
    })
  }

  async updateSnapshot(id, params) {
    const response = await this.client.basicPut<any>({
      url: `/snapshots/${id}`,
      body: {
        snapshot: params,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: `updateSnapshot`,
      },
    })
    return response.snapshot
  }

  async updateSnapshotMetadata(id, params) {
    const response = await this.client.basicPut<any>({
      url: `/snapshots/${id}/metadata`,
      body: {
        metadata: params,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: `updateSnapshotMetadata`,
      },
    })
    return response.metadata
  }

  async getDefaultQuotas() {
    const quotas = await this.client.basicGet<any>({
      url: `/os-quota-class-sets/defaults`,

      options: {
        clsName: this.getClassName(),
        mthdName: `getDefaultQuotas`,
      },
    })
    return quotas.quota_class_set
  }

  async getDefaultQuotasForRegion(region) {
    const urls = await this.getRegionUrls()
    const quotas = await this.client.basicGet<any>({
      endpoint: urls[region],
      url: `/os-quota-class-sets/defaults`,
      options: {
        clsName: this.getClassName(),
        mthdName: `getDefaultQuotasForRegion`,
      },
    })
    return quotas.quota_class_set
  }

  getTenantQuotas = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/os-quota-sets/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getCinderQuotas',
      },
    })
    return response.quota_set
  }

  getTenantQuotaUsage = async ({ id }) => {
    const response = await this.client.basicGet<any>({
      url: `/os-quota-sets/${id}?usage=true`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getCinderQuotaUsage',
      },
    })
    return response.quota_set
  }

  setTenantQuotas = async ({ id, body }) => {
    const quotas = await this.client.basicPut<any>({
      url: `/os-quota-sets/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: `setCinderQuotas`,
      },
    })
    return quotas.quota_set
  }

  async getQuotas(projectId) {
    const quota = await this.client.basicGet<any>({
      endpoint: `/os-quota-sets?usage=true`,
      url: `/os-quota-class-sets/defaults`,
      options: {
        clsName: this.getClassName(),
        mthdName: `getQuotas`,
      },
    })
    return quota.quota_set
  }

  async getQuotasForRegion(projectId, region) {
    const urls = await this.getRegionUrls()
    const quota = await this.client.basicGet<any>({
      endpoint: urls[region],
      url: `/os-quota-sets?usage=true`,
      options: {
        clsName: this.getClassName(),
        mthdName: `getQuotasForRegion`,
      },
    })
    return quota.quota_set
  }

  async setQuotas(params, projectId) {
    const quotas = await this.client.basicPut<any>({
      url: `/os-quota-sets`,
      body: {
        quota_set: params,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: `setQuotas`,
      },
    })
    return quotas.quota_set
  }

  async setQuotasForRegion(params, projectId, region) {
    const urls = await this.getRegionUrls()
    const quotas = await this.client.basicPut<any>({
      endpoint: urls[region],
      url: `/os-quota-sets`,
      body: {
        quota_set: params,
      },
      options: {
        clsName: this.getClassName(),
        mthdName: `setQuotasForRegion`,
      },
    })
    return quotas.quota_set
  }

  async getStoragePools() {
    const response = await this.client.basicGet<GetServerPoolsResponse>({
      url: `/scheduler-stats/get_pools?detail=True`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getStoragePools',
      },
    })
    return response.pools
  }

  async getStoarageServiceDetails() {
    const response = await this.client.basicGet<any>({
      url: `/os-services`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getStoarageServiceDetails',
      },
    })
    return response.services
  }
  async getVolumeTransfers() {
    const response = await this.client.basicGet<any>({
      url: '/volume-transfers/detail',
      options: {
        clsName: this.getClassName(),
        mthdName: 'getVolumeTransfers',
      },
      config: {
        headers: {
          'OpenStack-API-Version': 'volume 3.70',
        },
      },
    })
    return response.transfers
  }

  async getAllVolumeTransfers() {
    const response = await this.client.basicGet<any>({
      url: `/volume-transfers/detail?all_tenants=1`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getAllVolumeTransfers',
      },
      config: {
        headers: {
          'OpenStack-API-Version': 'volume 3.70',
        },
      },
    })
    return response.transfers
  }

  async createVolumeTransfer({ body }) {
    try {
      const response = await this.client.basicPost<any>({
        url: `/volume-transfers`,
        body,
        options: {
          clsName: this.getClassName(),
          mthdName: 'createVolumeTransfer',
        },
        config: {
          headers: {
            'OpenStack-API-Version': 'volume 3.70',
          },
        },
      })
      return response.transfer
    } catch (err) {
      return err.response.data
    }
  }

  async deleteVolumeTransfer(id) {
    return this.client.basicDelete<any>({
      url: `/volume-transfers/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteVolumeTransfer',
      },
      config: {
        headers: {
          'OpenStack-API-Version': 'volume 3.70',
        },
      },
    })
  }

  async acceptVolumeTransfer({ id, body }) {
    const response = await this.client.basicPost<any>({
      url: `/volume-transfers/${id}/accept`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'acceptVolumeTransfer',
      },
      config: {
        headers: {
          'OpenStack-API-Version': 'volume 3.70',
        },
      },
    })
    return response.transfer
  }

  async getCinderMessages() {
    const response = await this.client.basicGet<any>({
      url: `/messages`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getCinderMessages',
        config: {
          headers: {
            'OpenStack-API-Version': 'volume 3.70',
          },
        },
      },
    })
    return response.messages
  }

  // TODO: getStorageStats(need to implement host first)
}

export default Cinder
